import { Dom, Effect, Module, Player, type Webcam } from "@banuba/webar"
import data from "@banuba/webar/BanubaSDK.data?url"
import simd from "@banuba/webar/BanubaSDK.simd.wasm?url"
import wasm from "@banuba/webar/BanubaSDK.wasm?url"
import Eyes from "@banuba/webar/eyes.zip?url"
import frx_offline from "@banuba/webar/face_tracker.zip?url&include[]=flow/face_detector_offline.tflite&include[]=flow/face_detector_offline.tflite.bbin&include[]=flow/landmarks_224.tflite.bbin"
import Lips from "@banuba/webar/lips.zip?url"
import Makeup from "@banuba/webar/makeup.zip?url"
import Skin from "@banuba/webar/skin.zip?url"
import EmptyEffect from "./empty_effect.zip?url"
import * as Sources from "./image-sources"
import { Analysis as State } from "./state"
import * as Logger from "./logger"
import { typedEnv } from "../../config/typedEnv"

let _playerInstance: Player | null = null
let _playerPromise: Promise<Player> | null = null
let _mountedContainer: HTMLElement | null = null

export const createPlayer = async (): Promise<Player> => {
  if (!_playerPromise)
    _playerPromise = (async () => {
      const player = await Player.create({
        clientToken: typedEnv.VITE_SDK_TOKEN,
        locateFile: {
          "BanubaSDK.wasm": wasm,
          "BanubaSDK.simd.wasm": simd,
          "BanubaSDK.data": data,
        },
      })
      await player.addModule(...[frx_offline, Eyes, Lips, Makeup, Skin].map((m) => new Module(m)))
      _playerInstance = player
      return player
    })().catch((error) => {
      _playerPromise = null
      _playerInstance = null
      throw error
    })

  const player = await _playerPromise
  const effect = await Effect.preload(EmptyEffect)
  await player.applyEffect(effect)

  return player
}

export const getPlayer = () => _playerInstance
export const isMountedContainer = (container: HTMLElement | null | undefined) =>
  Boolean(container) && _mountedContainer === container

export const mount = (container: HTMLElement) => {
  if (_mountedContainer && _mountedContainer !== container) Dom.unmount(_mountedContainer)

  _mountedContainer = container
  Dom.render(_playerInstance!, container)
}

export const unmount = (container?: HTMLElement | null) => {
  const activeContainer = container ?? _mountedContainer
  if (!activeContainer || !isMountedContainer(activeContainer)) return

  Dom.unmount(activeContainer)
  _mountedContainer = null
}

export const setWaitingWhenImageChanged = async (_val: boolean) => {}
//#region prefabs

/**
 * NOTE:
 * makeup morphs must be placed in 'faces' field with prefix makeup_ and contain 3 fields:
 * color, coverage, finish
 *
 * example:
 * "faces": {
 *   "makeup_blush": {
 *     "color": "0 0 0",
 *     "coverage": "mid",
 *     "finish":
 *   }
 * }
 */
//#endregion

export const applyEffect = async (effect: Effect) => {
  if (!_playerInstance) return

  await _playerInstance.clearEffect()
  await _playerInstance.applyEffect(effect)
}

export const useWebcam = async (): Promise<Webcam> => {
  const webcam = Sources.get("webcam")
  if (webcam.active) webcam.cancelStop()

  if (State.source === webcam && webcam.active) return webcam
  if (!webcam.active) await webcam.start()

  if (!_playerInstance) _playerInstance = await createPlayer()

  _playerInstance.use((State.source = webcam), { horizontalFlip: true })
  _playerInstance.play({ pauseOnEmpty: false })
  await asyncRenderFrame()

  Logger.debug("Using Webcam for player")

  return webcam
}

const asyncRenderFrame = async () => {
  await new Promise((resolve) =>
    _playerInstance!.addEventListener("framereceived", resolve, { once: true })
  )
  await new Promise((resolve) =>
    _playerInstance!.addEventListener("frameprocessed", resolve, { once: true })
  )
  await new Promise((resolve) =>
    _playerInstance!.addEventListener("framerendered", resolve, { once: true })
  )
  await new Promise((resolve) =>
    _playerInstance!.addEventListener("framereceived", resolve, { once: true })
  )
  await new Promise((resolve) =>
    _playerInstance!.addEventListener("frameprocessed", resolve, { once: true })
  )
  await new Promise((resolve) =>
    _playerInstance!.addEventListener("framerendered", resolve, { once: true })
  )
  Logger.debug("Async Render Frame")
}

export const stopWebcam = () => {
  const webcam = Sources.get("webcam")
  if (State.source === webcam && webcam.active) webcam.scheduleStop()

  Logger.debug("Stop Webcam")
}

export const takePhoto = async (): Promise<Blob | undefined> => {
  const webcam = Sources.get("webcam")
  if (State.source === webcam && webcam.active) return webcam.takePhoto()
}
